// ─────────────────────────────────────────────────────────────────────────────
// Editor de un slide del Recap — paridad con `openWrappedSlideEditor` del demo
// (fundamentales-admin.js : 454341+): título en los 7 idiomas, fondo (color o foto), icono o
// etiquetas del dato dinámico, y vista previa que se actualiza mientras escribes.
//
// El slide de Ganador NO tiene icono editable: usa la insignia real configurada en Ganadores
// (el backend fuerza `icono = null` para esa clave).
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '@/i18n/languages';
import type { RecapSlideDto } from '@/api/recognition';

interface Props {
  slide: RecapSlideDto;
  /** true si el icono lo resuelve la plataforma (slide de ganador). */
  autoIcon: boolean;
  onCancel: () => void;
  onSave: (next: RecapSlideDto) => void;
}

/** Muestra de fondos del brandbook (los mismos colores que usan los slides del demo). */
const BG_SWATCHES = ['#1a1a2e', '#342F82', '#F08521', '#E11F26', '#20B15A', '#0D9488', '#8B5CF6'];

export function RecapSlideEditor({ slide, autoIcon, onCancel, onSave }: Readonly<Props>) {
  const { t, i18n } = useTranslation('recognition');
  const [draft, setDraft] = useState<RecapSlideDto>({
    ...slide,
    titulo: { ...slide.titulo },
    etiquetasNumero: [...(slide.etiquetasNumero ?? [])],
  });

  const labels = draft.etiquetasNumero ?? [];
  const hasNumbers = labels.length > 0;
  const previewTitle = draft.titulo[i18n.language] ?? draft.titulo.es ?? '';

  const setLang = (code: string, text: string) =>
    setDraft((d) => ({ ...d, titulo: { ...d.titulo, [code]: text } }));

  const setLabel = (idx: number, text: string) =>
    setDraft((d) => ({ ...d, etiquetasNumero: (d.etiquetasNumero ?? []).map((l, i) => (i === idx ? text : l)) }));

  // createPortal a document.body: `.plataforma-main` (recognition-shell.css) lleva `isolation:
  // isolate`, que aísla el z-index de `.modal-overlay` dentro de su propio contexto de apilamiento
  // — nunca compite contra el z-index explícito del Sidebar, que queda pintado encima. Mismo fix ya
  // aplicado en BulkUploadModal.tsx/WinnersImportWizardModal.tsx.
  return createPortal((
    <div className="modal-overlay modal-overlay--fade" role="dialog" aria-modal="true" onClick={onCancel}>
      <div className="adm-recap-editor" onClick={(e) => e.stopPropagation()}>
        <button className="rec-modal-close" onClick={onCancel} aria-label={t('common.close')}>
          <i className="fas fa-times" />
        </button>

        <h3 className="adm-recap-editor-title">
          <i className="fas fa-pen" /> {t('admin.recap.editorTitle', { index: draft.indice })}
        </h3>
        <p className="adm-note">{t(hasNumbers ? 'admin.recap.editorDescData' : 'admin.recap.editorDescIcon')}</p>

        <div className="adm-recap-editor-body">
          <div className="adm-recap-editor-fields">
            <div className="adm-field">
              <span>{t('admin.recap.titleLangs')}</span>
              <div className="adm-recap-lang-rows">
                {SUPPORTED_LANGUAGES.map((lng) => (
                  <label key={lng.code} className="adm-recap-lang-row">
                    <span className="adm-recap-lang">{lng.flag} {lng.displayCode}</span>
                    <input
                      type="text"
                      value={draft.titulo[lng.code] ?? ''}
                      placeholder={t('admin.recap.titlePlaceholder', { lang: lng.displayCode })}
                      onChange={(e) => setLang(lng.code, e.target.value)}
                    />
                  </label>
                ))}
              </div>
            </div>

            {autoIcon && (
              <p className="adm-note adm-note--info">
                <i className="fas fa-lock" /> {t('admin.recap.autoIcon')}
              </p>
            )}

            {!autoIcon && !hasNumbers && (
              <label className="adm-field">
                <span>{t('admin.recap.icon')}</span>
                <input
                  type="text"
                  className="adm-recap-icon-input"
                  value={draft.icono ?? ''}
                  placeholder={t('admin.recap.iconPlaceholder')}
                  onChange={(e) => setDraft((d) => ({ ...d, icono: e.target.value || null }))}
                />
              </label>
            )}

            {hasNumbers && (
              <div className="adm-field">
                <span>{t('admin.recap.numberLabels')}</span>
                <p className="adm-note">{t('admin.recap.numberLabelsHint')}</p>
                {/* Las etiquetas no tienen id propio: su posición ES su identidad (orden del slide). */}
                {labels.map((label, idx) => (
                  <input
                    key={`label-${idx}`}
                    type="text"
                    value={label}
                    placeholder={t('admin.recap.numberLabelPlaceholder')}
                    onChange={(e) => setLabel(idx, e.target.value)}
                  />
                ))}
              </div>
            )}

            <div className="adm-field">
              <span>{t('admin.recap.background')}</span>
              <div className="adm-recap-bg-row">
                <input
                  type="color"
                  aria-label={t('admin.recap.background')}
                  value={/^#[0-9a-f]{6}$/i.test(draft.fondo) ? draft.fondo : '#342F82'}
                  onChange={(e) => setDraft((d) => ({ ...d, fondo: e.target.value }))}
                />
                <div className="adm-chip-row">
                  {BG_SWATCHES.map((hex) => (
                    <button
                      key={hex}
                      type="button"
                      className={`adm-recap-swatch ${draft.fondo === hex ? 'adm-recap-swatch--on' : ''}`}
                      style={{ background: hex }}
                      aria-label={hex}
                      onClick={() => setDraft((d) => ({ ...d, fondo: hex }))}
                    />
                  ))}
                </div>
              </div>
              <input
                type="text"
                value={draft.fondo}
                placeholder="#342F82 | linear-gradient(...)"
                onChange={(e) => setDraft((d) => ({ ...d, fondo: e.target.value }))}
              />
            </div>
          </div>

          <div className="adm-recap-editor-preview">
            <span className="adm-recap-preview-label">{t('admin.recap.previewLabel')}</span>
            <div className="adm-recap-slide-preview" style={{ background: draft.fondo }}>
              {!autoIcon && !hasNumbers && <span className="adm-recap-slide-icon">{draft.icono}</span>}
              {hasNumbers && (
                <div className="adm-recap-slide-numbers">
                  {labels.map((label, idx) => (
                    <div key={`num-${idx}`}>
                      <strong>—</strong>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              )}
              {autoIcon && <span className="adm-recap-slide-icon"><i className="fas fa-award" /></span>}
              <p className="adm-recap-slide-title">{previewTitle}</p>
            </div>
            {hasNumbers && <p className="adm-note">{t('admin.recap.previewDataNote')}</p>}
          </div>
        </div>

        <div className="adm-recap-editor-actions">
          <button type="button" className="btn btn--secondary" onClick={onCancel}>{t('common.cancel')}</button>
          <button type="button" className="btn btn--primary" onClick={() => onSave(draft)}>
            <i className="fas fa-check" /> {t('admin.recap.apply')}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
